/** Where the user is rendered from — drives active highlight on global links. */
export type GlobalNavSurface =
    | "www_map"
    | "www_stats"
    | "www_other"
    | "todo"
    | "qr"
    | "opinion"
    | "styre"
    | "chat"
    | "kf"
    | "ledamot"
    | "kryss"
    | "valnatt";

export type GlobalNavItemId = "map" | "todo" | "qr" | "stats" | "chat" | "opinion" | "kf" | "ledamot" | "kryss" | "valnatt";

export type GlobalNavUrls = Record<GlobalNavItemId, string>;

const defaultSubdomains = {
    todo: "https://todo.centerreach.se/",
    qr: "https://qr.centerreach.se/",
    chat: "https://chat.centerreach.se/",
    opinion: "https://opinion.centerreach.se/",
    kf: "https://kf.centerreach.se/",
    ledamot: "https://ledamot.centerreach.se/",
    kryss: "https://kryss.centerreach.se/",
    valnatt: "https://valnatt.centerreach.se/"
} as const;

/**
 * Build absolute URLs for global nav. Pass wwwOrigin so map + stats stay on the same host as the main app (e.g. dev localhost).
 */
export function createGlobalNavUrls(wwwOrigin: string): GlobalNavUrls {
    const base = wwwOrigin.replace(/\/$/, "");
    return {
        map: `${base}/`,
        stats: `${base}/stats`,
        todo: defaultSubdomains.todo,
        qr: defaultSubdomains.qr,
        chat: defaultSubdomains.chat,
        opinion: defaultSubdomains.opinion,
        kf: defaultSubdomains.kf,
        ledamot: defaultSubdomains.ledamot,
        kryss: defaultSubdomains.kryss,
        valnatt: defaultSubdomains.valnatt
    };
}

export function getActiveNavItemId(surface: GlobalNavSurface): GlobalNavItemId | null {
    switch (surface) {
        case "www_map":
            return "map";
        case "www_stats":
            return "stats";
        case "todo":
            return "todo";
        case "qr":
            return "qr";
        case "opinion":
            return "opinion";
        case "styre":
        case "valnatt":
            return null;
        case "chat":
            return "chat";
        case "kf":
            return "kf";
        case "ledamot":
            return "ledamot";
        case "kryss":
            return "kryss";
        default:
            return null;
    }
}

export const globalNavItemOrder: GlobalNavItemId[] = ["map", "todo", "qr", "stats", "chat", "opinion", "kf", "ledamot", "kryss", "valnatt"];

export const globalNavLabelsSv: Record<GlobalNavItemId, string> = {
    map: "Karta",
    todo: "Todo",
    qr: "QR",
    stats: "Statistik",
    chat: "Chat",
    opinion: "Opinion",
    kf: "KF-arkiv",
    ledamot: "Ledamöter",
    kryss: "Kryss",
    valnatt: "Valnatt"
};

/** Label on the menu trigger: current app when known, otherwise "Meny". */
export function getGlobalNavTriggerLabel(surface: GlobalNavSurface): string {
    if (surface === "styre") {
        return "Styre";
    }
    if (surface === "valnatt") {
        return "Valnatt";
    }
    const id = getActiveNavItemId(surface);
    return id ? globalNavLabelsSv[id] : "Meny";
}
